import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface EditableEvaluationNameProps {
  name: string;
  onSave: (name: string) => void;
}

const MAX_NAME_LENGTH = 100;

/**
 * Editable evaluation name with inline editing
 */
export function EditableEvaluationName({ name, onSave }: EditableEvaluationNameProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    const trimmedValue = value.trim();

    // Validation
    if (!trimmedValue) {
      setError(t('dashboard:editName.errorRequired'));
      return;
    }

    if (trimmedValue.length > MAX_NAME_LENGTH) {
      setError(t('dashboard:editName.errorTooLong', { max: MAX_NAME_LENGTH }));
      return;
    }

    onSave(trimmedValue);
    setIsEditing(false);
    setError(null);
  };

  const handleCancel = () => {
    setValue(name);
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-3">
        <h1 className="text-4xl font-bold">{name}</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
          aria-label={t('dashboard:editName.editButton')}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          className="text-4xl font-bold h-auto py-2 border-2"
          maxLength={MAX_NAME_LENGTH}
          autoFocus
          aria-label={t('dashboard:editName.placeholder')}
          aria-invalid={!!error}
          aria-describedby={error ? 'name-error' : undefined}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 flex-shrink-0"
          aria-label={t('common:actions.save')}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive/90 flex-shrink-0"
          aria-label={t('common:actions.cancel')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {error && (
        <p id="name-error" role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {value.length}/{MAX_NAME_LENGTH} {t('dashboard:editName.characters')}
      </p>
    </div>
  );
}
